import React, { useState, useEffect } from 'react';
import { Button, Upload, message, Select, InputNumber, Progress } from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PlayCircleOutlined, 
  PauseCircleOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useTable, useDrawer, useConfirmModal } from '@/hooks/index';
import { GlobalTable, GlobalForm, ConfirmModal } from '@/index';
import type { TableColumn, TableAction } from '@/types/table';
import type { FormField } from '@/types/form';
import { appVersionsAPI } from '@/services/api/app-versions';
import { filesAPI } from '@/services/api/files';
import { deviceModelsAPI } from '@/services/api/device-models';
import type { AppVersion, AppVersionStatus, DeviceModelOption } from '@/types/api';
import { formatFileSize } from '@/utils/format';

/**
 * APK版本表单组件
 */
interface APKFormProps {
  mode: 'add' | 'edit';
  initialValues?: Partial<AppVersion>;
  onSuccess?: () => void;
}

const APKForm: React.FC<APKFormProps> = ({ mode, initialValues, onSuccess }) => {
  const { closeDrawer } = useDrawer();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deviceModelOptions, setDeviceModelOptions] = useState<DeviceModelOption[]>([]);

  // 加载机型选项列表
  useEffect(() => {
    const loadDeviceModels = async () => {
      try {
        const options = await deviceModelsAPI.getDeviceModelOptions();
        setDeviceModelOptions(options);
      } catch (error) {
        console.error('加载机型列表失败:', error);
        message.error('加载机型列表失败');
      }
    };
    loadDeviceModels();
  }, []);

  // 如果是编辑模式，初始化文件列表
  useEffect(() => {
    if (mode === 'edit' && initialValues?.file_name) {
      setFileList([{
        uid: '-1',
        name: initialValues.file_name,
        status: 'done',
      }]);
    }
  }, [mode, initialValues]);

  const formFields: FormField[] = [
    {
      name: 'model_id',
      label: '机型',
      type: 'custom',
      rules: [{ required: true, message: '请选择机型' }],
      customRender: () => (
        <Select
          placeholder="请选择机型"
          style={{ width: '100%' }}
          disabled={mode === 'edit'} // 编辑模式不允许修改机型
          options={deviceModelOptions.map(option => ({
            label: option.name,
            value: option.id,
          }))}
        />
      ),
    },
    {
      name: 'apkFile',
      label: 'APK文件',
      type: 'custom',
      rules: mode === 'add' ? [{ required: true, message: '请上传APK文件' }] : undefined,
      customRender: () => (
        <div>
          {uploading && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-sm text-blue-700 mb-2">正在上传APK文件...</div>
              <Progress 
                percent={uploadProgress} 
                status={uploadProgress === 100 ? 'success' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
            </div>
          )}
          <Upload.Dragger
            name="file"
            multiple={false}
            fileList={fileList}
            beforeUpload={(file) => {
              // 检查文件类型
              if (!file.name.toLowerCase().endsWith('.apk')) {
                message.error('只支持 .apk 格式的文件');
                return false;
              }
              // 检查文件大小（500MB）
              const maxSize = 500 * 1024 * 1024;
              if (file.size > maxSize) {
                message.error('文件大小不能超过 500MB');
                return false;
              }
              setFileList([file]);
              return false; // 阻止自动上传
            }}
            onRemove={() => {
              setFileList([]);
              return true;
            }}
            disabled={uploading}
            className="hover:border-blue-400"
            // 编辑模式也允许重新选择文件：如果选择了新文件会在提交时替换后端的 apk_file_id
          >
            <p className="ant-upload-drag-icon text-2xl">
              <UploadOutlined style={{ color: 'var(--color-primary)' }} />
            </p>
            <p className="ant-upload-text text-gray-600">点击或拖拽上传APK文件</p>
            <p className="ant-upload-hint text-gray-400">支持 .apk 格式, 最大 500MB</p>
          </Upload.Dragger>
        </div>
      ),
    },
    {
      name: 'version',
      label: '版本号',
      type: 'input',
      placeholder: '如: v3.2.1',
      rules: [{ required: true, message: '请输入版本号' }],
    },
    {
      name: 'version_code',
      label: '版本码',
      type: 'custom',
      rules: [{ required: true, message: '请输入版本码' }],
      customRender: () => (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="如: 321"
          min={1}
          precision={0}
        />
      ),
    },
    {
      name: 'description',
      label: '更新说明',
      type: 'textarea',
      placeholder: '描述本次更新的内容...',
      rows: 4,
    },
    {
      name: 'force_update',
      label: '强制更新',
      type: 'switch',
      extra: '设备必须安装此版本',
    },
  ];

  const handleFinish = async (values: any) => {
    try {
      setLoading(true);

      if (mode === 'add') {
        // 创建模式：需要先上传文件
        if (fileList.length === 0) {
          message.error('请先上传APK文件');
          setLoading(false);
          return;
        }

        // 兼容两种情况：
        // 1）fileList 存的是 antd 的 UploadFile（有 originFileObj）
        // 2）fileList 存的是原生 File（beforeUpload 直接塞进去）
        const uploadItem: any = fileList[0];
        const file: File = uploadItem.originFileObj || uploadItem;
        
        // 上传文件
        setUploading(true);
        setUploadProgress(0);
        try {
          const fileInfo = await filesAPI.uploadFile(file, (progress) => {
            setUploadProgress(progress);
          });
          
          // 创建版本
          await appVersionsAPI.createAppVersion({
            model_id: values.model_id,
            apk_file_id: fileInfo.id,
            version: values.version,
            version_code: values.version_code,
            description: values.description || '',
            // 后端要求 bool，这里显式转成布尔值
            force_update: !!values.force_update,
          });
          
          message.success('APK版本创建成功！');
          closeDrawer();
          onSuccess?.();
        } catch (error: any) {
          console.error('创建版本失败:', error);
          message.error(error.message || '创建版本失败，请重试！');
        } finally {
          setUploading(false);
          setUploadProgress(0);
        }
      } else {
        // 编辑模式：更新版本信息（可选替换APK文件）
        if (!initialValues?.id) {
          message.error('版本ID不存在');
          setLoading(false);
          return;
        }

        // 是否有新文件需要上传：
        // - 初始编辑时我们构造的占位项：{ uid: '-1', name, status: 'done' }，不需要上传
        // - 如果用户重新选择了文件，fileList[0] 会是 UploadFile/File，需要先上传再更新 apk_file_id
        let newApkFileId: number | undefined = undefined;

        if (fileList.length > 0) {
          const uploadItem: any = fileList[0];
          const isPlaceholder = uploadItem.uid === '-1' && !uploadItem.originFileObj;

          if (!isPlaceholder) {
            const file: File = uploadItem.originFileObj || uploadItem;

            // 上传新APK文件
            setUploading(true);
            setUploadProgress(0);
            try {
              const fileInfo = await filesAPI.uploadFile(file, (progress) => {
                setUploadProgress(progress);
              });
              newApkFileId = fileInfo.id;
            } catch (error: any) {
              console.error('上传APK文件失败:', error);
              message.error(error.message || '上传APK文件失败，请重试！');
              setUploading(false);
              setUploadProgress(0);
              setLoading(false);
              return;
            } finally {
              setUploading(false);
              setUploadProgress(0);
            }
          }
        }

        await appVersionsAPI.updateAppVersion(initialValues.id, {
          version: values.version,
          version_code: values.version_code,
          description: values.description,
          // 只在表单里有该字段时才更新，并且显式转成布尔值
          force_update: values.force_update !== undefined ? !!values.force_update : undefined,
          // 如果上传了新文件，则更新绑定的APK文件ID
          apk_file_id: newApkFileId,
        });
        
        message.success('APK版本更新成功！');
        closeDrawer();
        onSuccess?.();
      }
    } catch (error: any) {
      console.error('操作失败:', error);
      message.error(error.message || '操作失败，请重试！');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlobalForm
      fields={formFields}
      initialValues={{
        model_id: initialValues?.model_id,
        version: initialValues?.version,
        version_code: initialValues?.version_code,
        description: initialValues?.description,
        force_update: initialValues?.force_update,
      }}
      onFinish={handleFinish}
      loading={loading || uploading}
      submitText={mode === 'add' ? '创建版本' : '保存修改'}
    />
  );
};




const AppUpgradeManagement: React.FC = () => {
  const { tableRef, loading, setLoading, dataSource, setDataSource, pagination, updatePagination } = useTable<AppVersion & { key: string }>();
  const { openDrawer } = useDrawer();
  const { config, showConfirmModal, hideConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modelFilter, setModelFilter] = useState<number | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [deviceModelOptions, setDeviceModelOptions] = useState<DeviceModelOption[]>([]);

  // 获取App版本列表
  const fetchAppVersions = async (params?: { page?: number; size?: number; search?: string; status?: string; model_id?: number }) => {
    setLoading(true);
    try {
      // 构建请求参数
      const requestParams: any = {
        page: params?.page || pagination.current,
        size: params?.size || pagination.pageSize,
        search: params?.search !== undefined ? params.search : searchKeyword,
      };
      
      // 只有当显式传入的 status 存在且不是 'all' 时才传给后端
      if (params?.status && params.status !== 'all') {
        requestParams.status = params.status as AppVersionStatus;
      }
      
      // 只有当显式传入的 model_id 存在时才传给后端
      if (params?.model_id !== undefined) {
        requestParams.model_id = params.model_id;
      } else if (modelFilter !== undefined) {
        requestParams.model_id = modelFilter;
      }
      
      const response = await appVersionsAPI.getAppVersions(requestParams);
      
      // 如果list为null，代表数据为空
      const list = response.list || [];
      
      // 添加key字段用于表格
      const versionsWithKey = list.map(version => ({
        ...version,
        key: version.id.toString(),
      }));
      
      setDataSource(versionsWithKey);
      updatePagination(response.page, response.size, response.total);
    } catch (error: any) {
      console.error('获取App版本列表失败:', error);
      message.error('获取App版本列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载机型选项列表
  useEffect(() => {
    const loadDeviceModels = async () => {
      try {
        const options = await deviceModelsAPI.getDeviceModelOptions();
        setDeviceModelOptions(options);
      } catch (error) {
        console.error('加载机型列表失败:', error);
      }
    };
    loadDeviceModels();
  }, []);

  // 初始化数据
  useEffect(() => {
    fetchAppVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 获取状态样式
   */
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'publishing':
        return { color: 'success', text: '已发布' };
      case 'paused':
        return { color: 'warning', text: '已暂停' };
      case 'draft':
        return { color: 'default', text: '草稿' };
      case 'offline':
        return { color: 'default', text: '已下线' };
      default:
        return { color: 'default', text: '未知' };
    }
  };

  /**
   * 格式化文件大小
   */
  const formatAPKSize = (size?: number): string => {
    if (!size) return '-';
    return formatFileSize(size);
  };

  /**
   * 列配置
   */
  const columns: TableColumn<AppVersion & { key: string }>[] = [
    {
      title: '机型',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 150,
      render: (name: string) => name || '-',
    },
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      width: 120,
    },
    {
      title: '版本码',
      dataIndex: 'version_code',
      key: 'version_code',
      width: 100,
      render: (code: number) => (
        <span className="font-mono">{code}</span>
      ),
    },
    {
      title: 'APK大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 120,
      render: (size: number) => formatAPKSize(size),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, version: AppVersion & { key: string }): React.ReactNode => {
        const config = getStatusConfig(status);
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            config.color === 'success' ? 'bg-green-100 text-green-800' :
            config.color === 'warning' ? 'bg-orange-100 text-orange-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {version.status_name || config.text}
          </span>
        );
      },
    },
    // {
    //   title: '强制更新',
    //   dataIndex: 'force_update',
    //   key: 'force_update',
    //   width: 100,
    //   render: (forceUpdate: boolean) => (
    //     <span className={`px-2 py-1 rounded-full text-xs font-medium ${
    //       forceUpdate ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
    //     }`}>
    //       {forceUpdate ? '是' : '否'}
    //     </span>
    //   ),
    // },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => {
        if (!time) return '-';
        return new Date(time).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
  ];

  /**
   * 添加APK版本
   */
  const handleAdd = () => {
    openDrawer({
      title: '上传新版本',
      content: (
        <APKForm
          mode="add"
          onSuccess={() => {
            fetchAppVersions();
          }}
        />
      ),
      width: 600,
    });
  };

  /**
   * 编辑APK版本
   * - 先调用详情接口获取完整数据（包含文件名等）
   * - 再打开抽屉并将数据传给表单
   */
  const handleEdit = async (record: AppVersion & { key: string }) => {
    if (!record.id) {
      message.error('版本ID不存在');
      return;
    }

    try {
      setLoading(true);
      const detail = await appVersionsAPI.getAppVersionById(record.id);

      openDrawer({
        title: '编辑APK版本',
        content: (
          <APKForm
            mode="edit"
            initialValues={detail}
            onSuccess={() => {
              fetchAppVersions();
            }}
          />
        ),
        width: 600,
      });
    } catch (error: any) {
      console.error('获取版本详情失败:', error);
      message.error(error.message || '获取版本详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 配置发布策略功能暂时保留，后续可根据需要实现
  // const handleConfigStrategy = (record: AppVersion & { key: string }) => {
  //   openDrawer({
  //     title: '配置发布策略',
  //     content: (
  //       <StrategyConfig
  //         version={record}
  //         onSuccess={() => {
  //           fetchAppVersions();
  //         }}
  //       />
  //     ),
  //     width: 500,
  //   });
  // };

  /**
   * 确认操作
   * 注意：这里不再依赖 hook 内部的 config，而是通过闭包显式传入 type 和 data，
   * 避免由于状态异步更新导致拿到上一次的 config.data。
   */
  const handleConfirmAction = async (
    type: 'publish' | 'pause' | 'resume' | 'delete',
    data: { id: number; version: string }
  ) => {
    const versionId = data.id;

    if (!versionId || Number.isNaN(versionId)) {
      message.error('版本ID无效');
      return;
    }

    try {
      switch (type) {
        case 'publish':
          await appVersionsAPI.publishAppVersion({ version_id: versionId });
          message.success(`APK版本 ${data.version} 发布成功！`);
          break;
        case 'pause':
          await appVersionsAPI.pauseAppVersion({ version_id: versionId });
          message.success(`APK版本 ${data.version} 已暂停推送！`);
          break;
        case 'resume':
          await appVersionsAPI.resumeAppVersion({ version_id: versionId });
          message.success(`APK版本 ${data.version} 已恢复发布！`);
          break;
        case 'delete':
          await appVersionsAPI.deleteAppVersion(versionId);
          message.success(`APK版本 ${data.version} 删除成功！`);
          break;
      }
      hideConfirmModal();
      fetchAppVersions();
    } catch (error: any) {
      console.error('操作失败:', error);
      message.error(error.message || '操作失败，请重试！');
    }
  };

  /**
   * 发布/暂停/恢复APK
   */
  const handleTogglePublish = (record: AppVersion & { key: string }, type: 'publish' | 'pause' | 'resume') => {
    const actionMap: Record<string, string> = {
      publish: '发布',
      pause: '暂停',
      resume: '恢复',
    };
    
    showConfirmModal(type as 'publish' | 'pause' | 'resume', {
      data: { id: record.id.toString(), version: record.version },
      onConfirm: () =>
        handleConfirmAction(type, {
          id: record.id,
          version: record.version,
        }),
      onCancel: hideConfirmModal,
      title: `确认${actionMap[type]}版本`,
      content: `确定要${actionMap[type]}版本 ${record.version} 吗？`,
    });
  };

  /**
   * 删除APK版本
   */
  const handleDelete = (record: AppVersion & { key: string }) => {
    // 只有草稿状态的版本才允许删除
    // if (record.status !== 'draft') {
    //   message.warning('仅草稿状态的版本可以删除');
    //   return;
    // }

    showConfirmModal('delete', {
      data: { id: record.id.toString(), version: record.version },
      deleteConfig: {
        itemName: 'APK版本',
        warningText: '此操作无法撤销，请确认是否继续。',
        showWarning: true,
      },
      onConfirm: () =>
        handleConfirmAction('delete', {
          id: record.id,
          version: record.version,
        }),
      onCancel: hideConfirmModal,
    });
  };

  /**
   * 操作列配置
   */
  const actions: TableAction<AppVersion & { key: string }>[] = [
    {
      label: <EditOutlined />,
      key: 'edit',
      type: 'text',
      onClick: handleEdit,
      // 已发布状态不允许编辑
      hidden: (record) => record.status === 'publishing',
    },
    {
      label: <PauseCircleOutlined />,
      key: 'pause',
      type: 'text',
      onClick: (record) => handleTogglePublish(record, 'pause'),
      hidden: (record) => record.status !== 'publishing',
    },
    {
      label: <PlayCircleOutlined />,
      key: 'play',
      type: 'text',
      onClick: (record) => {
        if (record.status === 'paused') {
          handleTogglePublish(record, 'resume');
        } else {
          handleTogglePublish(record, 'publish');
        }
      },
      hidden: (record) => record.status === 'publishing',
    },
    {
      label: <DeleteOutlined />,
      key: 'delete',
      type: 'text',
      danger: true,
      onClick: handleDelete,
      // 发布中状态不可删除，其他状态可以删除
      hidden: (record) => record.status === 'publishing',
    },
  ];

  /**
   * 处理搜索
   */
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    updatePagination(1, pagination.pageSize);
    fetchAppVersions({
      page: 1,
      size: pagination.pageSize,
      search: value,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      model_id: modelFilter,
    });
  };

  /**
   * 处理状态筛选
   */
  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    updatePagination(1, pagination.pageSize);
    fetchAppVersions({
      page: 1,
      size: pagination.pageSize,
      status: value !== 'all' ? value : undefined,
      model_id: modelFilter,
    });
  };

  /**
   * 处理机型筛选
   */
  const handleModelFilter = (value: number | undefined) => {
    setModelFilter(value);
    updatePagination(1, pagination.pageSize);
    fetchAppVersions({
      page: 1,
      size: pagination.pageSize,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      model_id: value,
    });
  };

  /**
   * 处理分页变化
   */
  const handlePaginationChange = (page: number, pageSize: number) => {
    updatePagination(page, pageSize);
    fetchAppVersions({
      page,
      size: pageSize,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      model_id: modelFilter,
    });
  };

  return (
    <>
      <div className="space-y-6">
        <GlobalTable
          ref={tableRef}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          header={{
            title: 'App升级管理',
            subtitle: '管理OTA应用APK的升级版本与发布策略',
            extra: (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={handleAdd}
                className="bg-blue-600 hover:bg-blue-700"
              >
                上传新版本
              </Button>
            ),
          }}
          toolbar={{
            showSearch: true,
            searchPlaceholder: '搜索版本号、版本码或机型...',
            onSearch: handleSearch,
            actions: [
              <Select
                key="model-filter"
                value={modelFilter}
                onChange={handleModelFilter}
                placeholder="全部机型"
                allowClear
                style={{ width: 150 }}
                size="large"
                options={deviceModelOptions.map(option => ({
                  label: option.name,
                  value: option.id,
                }))}
              />,
              <Select
                key="status-filter"
                value={statusFilter}
                onChange={handleStatusFilter}
                style={{ width: 120 }}
                size="large"
                options={[
                  { label: '全部状态', value: 'all' },
                  { label: '已发布', value: 'publishing' },
                  { label: '已暂停', value: 'paused' },
                  { label: '草稿', value: 'draft' },
                  { label: '已下线', value: 'offline' },
                ]}
              />,
            ],
          }}
          actions={actions}
          actionsWidth={150}
          pagination={{
            ...pagination,
            showTotal: true,
            showSizeChanger: true,
            onChange: handlePaginationChange,
          }}
          scroll={{ x: 1000 }}
        />
      </div>
      
      {/* 全局确认框 */}
      <ConfirmModal 
        config={{
          ...config,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        }}
      />
    </>
  );
};

export default AppUpgradeManagement;
